// apps/client/src/world/terrain/water.ts — one resin water mesh (sea, rivers, ponds, waterfalls) plus its beds, in two material groups
import { tuning } from '@whiskerton/content';
import { groundAt, tierAt, type MapData } from '@whiskerton/shared';
import * as THREE from 'three';
import type { LightValues } from '../../engine/lighting';
import { SEA_LEVEL, waterSurface } from './field';
import { ripplesTexture } from './textures';

type Tiles = Pick<MapData, 'w' | 'h' | 'tiles'>;
/** (x, y) = the higher tile; dir 0 N, 1 E, 2 S, 3 W toward the lower one; top/bottom = the two water surfaces (revision 9 item 10). */
export interface WaterfallEdge { x: number; y: number; dir: 0 | 1 | 2 | 3; top: number; bottom: number }
const DIRS = [[0, -1], [1, 0], [0, 1], [-1, 0]] as const;
const inside = (m: Tiles, x: number, y: number) => x >= 0 && y >= 0 && x < m.w && y < m.h;
const surfaceAt = (m: Tiles, x: number, y: number): number | null => {
  const g = groundAt(m, x, y);
  return g === 'sea' ? SEA_LEVEL : g === 'river' || g === 'pond' ? waterSurface(tierAt(m, x, y)) : null;
};

/** Every river/pond tile whose 4-neighbour is water with a surface more than 0.1 lower gets an edge on its side facing that neighbour. */
export function waterfallEdges(map: Tiles): WaterfallEdge[] {
  const out: WaterfallEdge[] = [];
  for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
    const g = groundAt(map, x, y);
    if (g !== 'river' && g !== 'pond') continue;
    const top = waterSurface(tierAt(map, x, y));
    DIRS.forEach(([dx, dy], dir) => {
      if (!inside(map, x + dx, y + dy)) return;
      const bottom = surfaceAt(map, x + dx, y + dy);
      if (bottom !== null && top - bottom > 0.1) out.push({ x, y, dir: dir as WaterfallEdge['dir'], top, bottom });
    });
  }
  return out;
}

const BED = new THREE.Color('#2A7E98'), FOAM = new THREE.Color('#F2F5EC'), CLAY = new THREE.Color('#B78C60');
const UV = 1 / 400;                                           // ripples repeat 30 per 400 units; every water surface shares the mapping
const SEA_CELL = 4, SEA_MARGIN = 72;                          // the sea sheet reaches past the horizon around the map
const SHEET_OUT = 0.05, BED_WALL_OUT = 0.02;                  // the sheet stands just proud of the dark wall behind it

/** Accumulates quads into the two groups of one geometry: 0 = water (resin), 1 = beds, walls and foam (vertex colours). */
class Quads {
  private readonly pos: number[][] = [[], []];
  private readonly nor: number[][] = [[], []];
  private readonly uv: number[][] = [[], []];
  private readonly col: number[][] = [[], []];
  /** a, b, c, d counter-clockwise seen from the side the normal points to. */
  add(group: 0 | 1, pts: readonly (readonly [number, number, number])[], n: readonly [number, number, number], uvs: readonly (readonly [number, number])[], c: THREE.Color): void {
    for (const k of [0, 1, 2, 0, 2, 3]) {
      this.pos[group]!.push(...pts[k]!); this.nor[group]!.push(...n); this.uv[group]!.push(...uvs[k]!); this.col[group]!.push(c.r, c.g, c.b);
    }
  }
  build(): THREE.BufferGeometry {
    const geo = new THREE.BufferGeometry();
    const flat = (a: number[][]) => new Float32Array([...a[0]!, ...a[1]!]);
    geo.setAttribute('position', new THREE.BufferAttribute(flat(this.pos), 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(flat(this.nor), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(flat(this.uv), 2));
    geo.setAttribute('color', new THREE.BufferAttribute(flat(this.col), 3));
    const n0 = this.pos[0]!.length / 3, n1 = this.pos[1]!.length / 3;
    geo.addGroup(0, n0, 0);
    geo.addGroup(n0, n1, 1);
    return geo;
  }
}

export function buildWater(map: MapData): { mesh: THREE.Mesh; material: THREE.MeshStandardMaterial; update(dt: number, light: LightValues): void } {
  const q = new Quads(), rise = tuning.map.tierRise;
  const cx = map.w / 2, cz = map.h / 2;
  const flatUv = (x: number, z: number): [number, number] => [(x - cx) * UV + 0.5, (cz - z) * UV + 0.5];
  const level = (x0: number, z0: number, x1: number, z1: number, y: number, group: 0 | 1, c: THREE.Color) =>
    q.add(group, [[x0, y, z1], [x1, y, z1], [x1, y, z0], [x0, y, z0]], [0, 1, 0], [flatUv(x0, z1), flatUv(x1, z1), flatUv(x1, z0), flatUv(x0, z0)], c);
  /** A vertical quad on the edge of tile (x, y) facing dir, `out` beyond the edge, from y0 up to y1. */
  const wall = (x: number, y: number, dir: number, y0: number, y1: number, out: number, group: 0 | 1, c: THREE.Color, sheetUv: boolean) => {
    const [dx, dy] = DIRS[dir]!;
    const mx = x + 0.5 + dx * (0.5 + out), mz = y + 0.5 + dy * (0.5 + out);   // the edge's midpoint
    const tx = -dy * 0.5, tz = dx * 0.5;                                        // half the edge, turning left of the facing
    const a: [number, number, number] = [mx + tx, y0, mz + tz], b: [number, number, number] = [mx - tx, y0, mz - tz];
    const pts = [a, b, [b[0], y1, b[2]], [a[0], y1, a[2]]] as const;
    const along = (p: readonly [number, number, number]) => ((dx === 0 ? p[0] : p[2]) - (dx === 0 ? cx : cz)) * UV + 0.5;
    const vy = (yy: number) => yy * UV * (sheetUv ? 0.1 : 1);                     // stretched: the drifting ripples read as falling water
    q.add(group, pts, [dx, 0, dy], pts.map((p) => [along(p), vy(p[1])] as const), c);
  };

  // The sea: one sheet under the whole view, cut into SEA_CELL squares so the world shader's per-vertex bend stays smooth (a 400-unit
  // quad would sag by tens of units in the middle). Rivers crossing the beach at tier 0 share it: their channel is cut into the sand.
  const seaFrom = Math.floor(-SEA_MARGIN / SEA_CELL) * SEA_CELL;
  for (let z = seaFrom; z < map.h + SEA_MARGIN; z += SEA_CELL) for (let x = seaFrom; x < map.w + SEA_MARGIN; x += SEA_CELL) {
    level(x, z, x + SEA_CELL, z + SEA_CELL, SEA_LEVEL, 0, BED);
  }
  // Rivers and ponds above the beach: a surface and a dark bed per tile. The terrace hole they sit in is smoothed, so it reaches into
  // the banks: every quarter of a bank tile next to such water (across an edge or a corner) gets one claimant's surface and bed too,
  // hidden under the terrace where the bank stands and filling the hole where it doesn't, never doubled up.
  const lake = (x: number, y: number): number | null => {
    const g = groundAt(map, x, y), t = tierAt(map, x, y);
    return inside(map, x, y) && (g === 'river' || g === 'pond') && t > 0 ? t : null;
  };
  const patch = (x0: number, z0: number, x1: number, z1: number, t: number) => {
    level(x0, z0, x1, z1, waterSurface(t), 0, BED);
    level(x0, z0, x1, z1, t * rise - 0.55, 1, BED);
  };
  for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
    const t = lake(x, y);
    if (t !== null) { patch(x, y, x + 1, y + 1, t); continue; }
    if (surfaceAt(map, x, y) !== null) continue;                                   // sea or tier-0 river: the sea sheet covers it
    const bank = tierAt(map, x, y);
    for (const qy of [0, 1]) for (const qx of [0, 1]) {
      const sx = qx ? 1 : -1, sy = qy ? 1 : -1;
      const claim = [lake(x, y + sy), lake(x + sx, y), lake(x + sx, y + sy)].find((c): c is number => c !== null && c <= bank);
      if (claim !== undefined) patch(x + qx * 0.5, y + qy * 0.5, x + qx * 0.5 + 0.5, y + qy * 0.5 + 0.5, claim);
    }
  }
  // Waterfalls: a resin sheet per tier step, a dark wall behind it and a foam strip under the water where it lands (the sea sheet for tier 0).
  for (const e of waterfallEdges(map)) {
    const [dx, dy] = DIRS[e.dir]!, lowTier = tierAt(map, e.x + dx, e.y + dy);
    const bottom = lowTier === 0 ? SEA_LEVEL : e.bottom, bed = e.top - 0.3, lowBed = lowTier === 0 ? -0.6 : e.bottom - 0.3;
    wall(e.x, e.y, e.dir, bottom, e.top, SHEET_OUT, 0, BED, true);
    wall(e.x, e.y, e.dir, lowBed, bed, BED_WALL_OUT, 1, BED, false);
    // The foam lies just under the lower surface, so the water over it softens it.
    const ex = e.x + 0.5 + dx * (0.5 + SHEET_OUT), ez = e.y + 0.5 + dy * (0.5 + SHEET_OUT), fy = bottom - 0.02;
    const fx0 = Math.min(ex - Math.abs(dy) * 0.5, ex + dx * 0.25), fx1 = Math.max(ex + Math.abs(dy) * 0.5, ex + dx * 0.25);
    const fz0 = Math.min(ez - Math.abs(dx) * 0.5, ez + dy * 0.25), fz1 = Math.max(ez + Math.abs(dx) * 0.5, ez + dy * 0.25);
    level(fx0, fz0, fx1, fz1, fy, 1, FOAM);
  }
  // Banks: where water sits above dry land of a lower tier (a river leaving its plateau sideways), a clay bank holds it in.
  for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
    const g = groundAt(map, x, y);
    if (g !== 'river' && g !== 'pond') continue;
    const t = tierAt(map, x, y);
    DIRS.forEach(([dx, dy], dir) => {
      if (!inside(map, x + dx, y + dy) || surfaceAt(map, x + dx, y + dy) !== null) return;
      const nt = tierAt(map, x + dx, y + dy);
      if (nt < t) wall(x, y, dir, nt * rise - 0.6, t * rise, 0, 1, CLAY, false);
    });
  }

  const ripples = ripplesTexture();
  const material = new THREE.MeshStandardMaterial({ color: '#6CCBDB', transparent: true, opacity: 0.66, roughness: 0.5, metalness: 0,
    normalMap: ripples, normalScale: new THREE.Vector2(0.35, 0.35) });
  const beds = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(q.build(), [material, beds]);
  mesh.name = 'water';
  mesh.receiveShadow = true;
  let t = 0;
  return {
    mesh, material,
    update(dt, light) {
      t += dt;
      ripples.offset.set(t * 0.004, t * 0.0025);
      mesh.position.y = Math.sin(t * 1.1) * 0.012;
      material.color.copy(light.water);
      material.opacity = light.waterOpacity;
    },
  };
}
